import { formatNumber } from '@belong/common';
import { Text, BulletList } from '@belong/ui';
import { UnitListingDetailsModel } from 'src/api/models';
import { FavoriteButton } from 'src/features/listing-page/components/favorite-button/favorite-button';
import type { NormalizedFavorites } from 'src/features/listing-page/types';
import {
  getBathRoomString,
  getFormattedBeds,
} from 'src/features/listing-page/utils';
import { formatAddress } from '../../utils';
import { ApplyButton } from '../apply-button/apply-button';
import { RentAmount } from '../rent-amount/rent-amount';

type StickyBannerProps = {
  home: UnitListingDetailsModel;
  style: React.CSSProperties;
  normalizedFavoritesByHomeId: NormalizedFavorites;
};

export function StickyBanner({
  home,
  style,
  normalizedFavoritesByHomeId,
}: StickyBannerProps) {
  const { basicInfo, listingInfo } = home;
  const { isDemoUnit, isAvailableForRent, isComingSoon, rentAmount } =
    listingInfo || {};

  // `!` throughout: this banner only renders inside the house page, which
  // already dereferences `address`/`basicInfo` unconditionally.
  const address = formatAddress(home.address!, home.basicInfo!.unitNumber);

  // Coming Soon homes have pricing already set and published, even though
  // isAvailableForRent stays false until Full Listing Published (LOVE-335).
  const shouldShowRentAmount =
    !isDemoUnit && !!(isAvailableForRent || isComingSoon) && !!rentAmount;

  return (
    <div
      style={style}
      className="fixed top-2xl z-50 hidden w-full bg-white py-xs shadow xl:block"
    >
      <div className="container flex h-full items-center justify-between">
        <div className="flex flex-col items-start">
          <Text className="mt-0 text-p1">{address}</Text>
          <div className="flex gap-sm">
            <Text
              as="div"
              variant="h3"
              fontWeight="semibold"
              className="leading-p2"
            >
              <BulletList
                wrap
                items={[
                  getFormattedBeds(basicInfo),
                  getBathRoomString(basicInfo!),
                  <span key="sqft">
                    {formatNumber(basicInfo!.areaInSquareFeet!)} ft<sup>2</sup>
                  </span>,
                ]}
              />
            </Text>
            <FavoriteButton
              homeId={basicInfo!.unitId!}
              isFavorite={!!normalizedFavoritesByHomeId[basicInfo!.unitId!]}
              key={basicInfo!.unitId}
            />
          </div>
        </div>
        <div className="flex items-center gap-2xl pl-md">
          {shouldShowRentAmount && (
            <RentAmount rentAmount={rentAmount} priceVariant="h3" />
          )}
          <ApplyButton
            homeId={home.basicInfo?.unitId}
            listingId={home.listingInfo?.uniqueId}
            isDemoUnit={home.listingInfo?.isDemoUnit}
            isAvailableForRent={home.listingInfo?.isAvailableForRent}
            isComingSoon={home.listingInfo?.isComingSoon}
            availabilityStatus={home.listingInfo?.availabilityStatus}
            size="small"
            className="ml-sm"
          />
        </div>
      </div>
    </div>
  );
}
